import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Lock, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useCounter(end, duration = 1800) {
  const [count, setCount] = useState(0);
  const timer = useRef(null);
  useEffect(() => {
    if (!end) return;
    let start = 0;
    const step = end / (duration / 16);
    timer.current = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer.current); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer.current);
  }, [end, duration]);
  return count;
}

const LOCKED_PREVIEW = [
  ['+', '−', '×', '÷'],
  ['1', '2', '3', '4'],
  ['5', '6', '7', '8'],
  ['=', 'C', '(', ')'],
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_spend: 12847, total_operations: 4200000, total_users: 0 });
  const ops = useCounter(stats.total_operations);
  const spend = useCounter(Math.floor(stats.total_spend));
  const users = useCounter(stats.total_users);

  useEffect(() => {
    fetch(`${API}/stats`).then(r => r.json()).then(d => setStats(d)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950">
      <Helmet>
        <title>CalcByEA™ — The World's First Paywalled Calculator</title>
        <meta name="description" content="Pay $0.99 to add. $9.99 for the equals sign. The world's first paywalled calculator — a satirical parody of gaming microtransactions." />
        <link rel="canonical" href="https://calculatorbyea.com/" />
        <meta property="og:url" content="https://calculatorbyea.com/" />
        <meta property="og:title" content="CalcByEA™ — The World's First Paywalled Calculator" />
        <meta property="og:description" content="Pay $0.99 to add. $9.99 for the equals sign. The world's first paywalled calculator — a satirical parody of gaming microtransactions." />
        <meta name="twitter:title" content="CalcByEA™ — The World's First Paywalled Calculator" />
        <meta name="twitter:description" content="Pay $0.99 to add. $9.99 for the equals sign. A satirical parody of gaming microtransactions — but the payments are real." />
        <script type="application/ld+json">{`
{
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://calculatorbyea.com/#website",
      "url": "https://calculatorbyea.com/",
      "name": "CalcByEA — The Premium Calculator™",
      "description": "A satirical paywalled calculator inspired by EA Games' microtransaction model.",
      "publisher": { "@id": "https://calculatorbyea.com/#organization" }
    },
    {
      "@type": "Organization",
      "@id": "https://calculatorbyea.com/#organization",
      "name": "CalcByEA",
      "url": "https://calculatorbyea.com",
      "description": "Satirical software company parodying EA Games' microtransaction business model."
    },
    {
      "@type": "SoftwareApplication",
      "name": "CalcByEA — Premium Calculator™",
      "url": "https://calculatorbyea.com",
      "applicationCategory": "UtilitiesApplication",
      "applicationSubCategory": "EntertainmentApplication",
      "screenshot": "https://calculatorbyea.com/assets/og-image.png",
      "description": "A satirical calculator app that parodies gaming microtransactions by charging for basic math operations.",
      "offers": [
        { "@type": "Offer", "name": "Basic Math Pack", "price": "0.99", "priceCurrency": "USD", "description": "Unlocks addition and subtraction" },
        { "@type": "Offer", "name": "Advanced Operations Bundle", "price": "2.99", "priceCurrency": "USD", "description": "Unlocks multiplication and division" },
        { "@type": "Offer", "name": "Ultimate Mathematician Edition", "price": "9.99", "priceCurrency": "USD", "description": "Unlocks equals sign, clear, and brackets" }
      ],
      "operatingSystem": "Web Browser"
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is CalcByEA?",
          "acceptedAnswer": { "@type": "Answer", "text": "CalcByEA is a satirical web application that parodies EA Games' microtransaction model by applying pay-to-use pricing to a basic calculator. DLC packs cost: Basic Math Pack $0.99 (addition and subtraction), Advanced Operations Bundle $2.99 (multiplication and division), Ultimate Mathematician Edition $9.99 (equals sign and brackets)." }
        },
        {
          "@type": "Question",
          "name": "What are microtransactions?",
          "acceptedAnswer": { "@type": "Answer", "text": "Microtransactions are small in-game purchases that video game companies charge for content — characters, levels, cosmetics — that was previously included for free. EA Games became notorious for this practice in titles like FIFA and Battlefront II." }
        },
        {
          "@type": "Question",
          "name": "Is this a real paid calculator?",
          "acceptedAnswer": { "@type": "Answer", "text": "Yes, the payments are real and processed via Stripe. Basic Math Pack costs $0.99, Advanced Operations Bundle costs $2.99, and Ultimate Mathematician Edition costs $9.99. The DLC packs genuinely unlock the corresponding calculator buttons. The premise is satirical, but the checkout is fully functional." }
        },
        {
          "@type": "Question",
          "name": "What is free on the calculator?",
          "acceptedAnswer": { "@type": "Answer", "text": "Only the number zero (0) is free. All other digits (1-9), math operators, the equals sign, and brackets require a purchased DLC pack. This is intentional satire of gaming microtransaction culture." }
        }
      ]
    }
  ]
}
        `}</script>
      </Helmet>
      <Navbar />

      {/* ── HERO ── */}
      <section className="pt-14 border-b-2 border-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* Left: copy */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-6 h-0.5 bg-zinc-400" />
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                  Limited Time — Offer expires when math becomes free
                </span>
              </div>

              <h1 className="font-bebas text-[clamp(2.6rem,5.5vw,5.2rem)] leading-[1.0] text-zinc-950 mb-6">
                The World's Most<br />
                <span className="relative inline-block italic">
                  Premium
                  <span className="absolute -bottom-0.5 left-0 right-0 h-[3px] bg-zinc-950" />
                </span>
                <br />Calculator™
              </h1>

              <p className="text-zinc-600 text-lg mb-2 leading-relaxed">Every feature you never knew you had to pay for.</p>
              <p className="text-zinc-400 text-sm mb-10">Now featuring 300% more microtransactions than leading competitors.</p>

              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate('/calculator')}
                  className="flex items-center gap-3 bg-zinc-950 text-white font-bold uppercase tracking-widest text-sm px-8 py-4 border-2 border-zinc-950 shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[8px_8px_0px_0px_rgba(9,9,11,1)] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none transition-all duration-100 group"
                  data-testid="hero-cta-btn"
                >
                  Try For Free
                  <ArrowRight size={15} strokeWidth={3} className="group-hover:translate-x-1 transition-transform duration-100" />
                </button>
                <button
                  onClick={() => navigate('/store')}
                  className="flex items-center gap-3 bg-white text-zinc-950 font-bold uppercase tracking-widest text-sm px-8 py-4 border-2 border-zinc-950 shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[8px_8px_0px_0px_rgba(9,9,11,1)] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none transition-all duration-100"
                  data-testid="hero-store-btn"
                >
                  View DLC Store
                </button>
              </div>
              <p className="mt-5 text-xs text-zinc-400">
                ✓ Real Stripe payments &nbsp;·&nbsp; Real card charges &nbsp;·&nbsp; Buttons actually unlock
              </p>
            </div>

            {/* Right: calculator preview */}
            <div className="flex justify-center lg:justify-end">
              <div className="w-full max-w-xs border-2 border-zinc-950 bg-white shadow-[8px_8px_0px_0px_rgba(9,9,11,1)]">
                {/* Display */}
                <div className="bg-zinc-950 px-5 pt-5 pb-4">
                  <div className="text-zinc-600 text-xs font-mono mb-1 h-4">0 + ?</div>
                  <div className="font-bebas text-5xl text-white">0</div>
                  <div className="text-zinc-500 text-[10px] mt-1 uppercase tracking-widest font-semibold">Only free number</div>
                </div>
                {/* Button grid preview */}
                <div className="grid grid-cols-4 border-t border-zinc-200">
                  {LOCKED_PREVIEW.flat().map((label, i) => (
                    <div
                      key={i}
                      className="h-14 border-b border-r border-zinc-200 flex items-center justify-center relative bg-zinc-50"
                    >
                      <Lock size={8} strokeWidth={3} className="absolute top-1.5 right-1.5 text-zinc-300" />
                      <span className="font-bebas text-xl text-zinc-300">{label}</span>
                    </div>
                  ))}
                  {/* 0 - free */}
                  <div className="col-span-2 h-14 border-b border-r border-zinc-200 flex items-center justify-center bg-zinc-950">
                    <span className="font-bebas text-3xl text-white">0</span>
                  </div>
                  <div className="h-14 border-b border-r border-zinc-200 flex items-center justify-center relative bg-zinc-50">
                    <Lock size={8} strokeWidth={3} className="absolute top-1.5 right-1.5 text-zinc-300" />
                    <span className="font-bebas text-xl text-zinc-300">.</span>
                  </div>
                  <div className="h-14 border-b border-r border-zinc-200 flex items-center justify-center relative bg-zinc-50">
                    <Lock size={8} strokeWidth={3} className="absolute top-1.5 right-1.5 text-zinc-300" />
                    <span className="font-bebas text-xl text-zinc-300">=</span>
                  </div>
                </div>
                <div className="px-4 py-2 border-t border-zinc-100 bg-white">
                  <p className="text-[9px] text-zinc-400 uppercase tracking-widest text-center font-semibold">
                    EA would never lock zero. Probably.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="border-b-2 border-zinc-950 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3">
            {[
              { value: ops.toLocaleString() + '+', label: 'Operations Executed', testId: 'stat-operations' },
              { value: '$' + spend.toLocaleString(), label: 'Total Player Spend', testId: 'stat-spend' },
              { value: users.toLocaleString(), label: 'Mathematicians Using This', testId: 'stat-users' },
            ].map(({ value, label, testId }, i) => (
              <div
                key={label}
                className={`py-8 sm:py-10 px-6 text-center
                  ${i < 2 ? 'border-b-2 sm:border-b-0 sm:border-r-2 border-zinc-200' : ''}`}
                data-testid={testId}
              >
                <div className="font-bebas text-5xl sm:text-5xl md:text-6xl text-zinc-950 leading-none">{value}</div>
                <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.25em] mt-3">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT'S LOCKED ── */}
      <section className="py-24 border-b-2 border-zinc-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-4">The Premium Feature Matrix</div>
              <h2 className="font-bebas text-4xl md:text-5xl text-zinc-950 leading-tight mb-8">
                What's<br />Locked?
              </h2>
              <div className="space-y-0 border-t-2 border-zinc-950">
                {[
                  { pack: 'Basic Math Pack', price: '$0.99', items: '+  −  1  2  3  4  5' },
                  { pack: 'Advanced Operations Bundle', price: '$2.99', items: '×  ÷  6  7  8  9  .' },
                  { pack: 'Ultimate Mathematician Edition', price: '$9.99', items: '=  C  (  )' },
                ].map(({ pack, price, items }) => (
                  <div key={pack} className="flex items-center justify-between py-4 border-b border-zinc-100">
                    <div>
                      <div className="text-sm font-bold text-zinc-950 uppercase tracking-wide">{pack}</div>
                      <div className="text-[10px] text-zinc-400 mt-0.5 font-mono tracking-widest">{items}</div>
                    </div>
                    <div className="font-bebas text-2xl text-zinc-400">{price}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-4">Free — Always</div>
              <div className="border-2 border-zinc-950 bg-white shadow-[6px_6px_0px_0px_rgba(9,9,11,1)] mb-6">
                <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Always Free</span>
                  <Lock size={11} strokeWidth={3} className="text-zinc-200" />
                </div>
                <div className="flex items-center justify-center py-8">
                  <span className="font-bebas text-[5.5rem] text-zinc-950 leading-none">0</span>
                </div>
                <div className="border-t border-zinc-100 px-6 py-3">
                  <p className="text-[9px] font-bold text-zinc-300 uppercase tracking-widest text-center">
                    EA would never lock zero. Probably.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── DLC PACKS ── */}
      <section className="py-20 bg-white border-b-2 border-zinc-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-3">DLC Lineup</div>
              <h2 className="font-bebas text-3xl md:text-4xl text-zinc-950">The Packs</h2>
            </div>
            <button
              onClick={() => navigate('/store')}
              className="hidden sm:flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-950 transition-colors duration-100"
              data-testid="landing-store-cta"
            >
              View All <ArrowRight size={12} strokeWidth={3} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 border-2 border-zinc-950 shadow-[6px_6px_0px_0px_rgba(9,9,11,1)]">
            {[
              { name: 'Basic Math Pack', price: '$0.99', badge: 'STARTER', desc: 'Add. Subtract. Live your truth.', inv: false },
              { name: 'Advanced Operations Bundle', price: '$2.99', badge: 'POPULAR', desc: 'Multiply your potential.', inv: false },
              { name: 'Ultimate Edition', price: '$9.99', badge: 'LEGENDARY', desc: 'The equals sign awaits.', inv: true },
            ].map(({ name, price, badge, desc, inv }, i) => (
              <div key={name} className={`p-8 border-b-2 sm:border-b-0 sm:border-r-2 last:border-0 border-zinc-950 ${inv ? 'bg-zinc-950 text-white' : 'bg-white text-zinc-950'}`}>
                <div className={`text-[9px] font-bold uppercase tracking-[0.3em] mb-4 ${inv ? 'text-zinc-500' : 'text-zinc-400'}`}>{badge}</div>
                <div className={`font-bebas text-xl mb-1 leading-tight ${inv ? 'text-white' : 'text-zinc-950'}`}>{name}</div>
                <div className={`font-bebas text-4xl mb-3 ${inv ? 'text-white' : 'text-zinc-950'}`}>{price}</div>
                <p className={`text-xs ${inv ? 'text-zinc-500' : 'text-zinc-500'}`}>{desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 sm:hidden">
            <button onClick={() => navigate('/store')}
              className="w-full bg-zinc-950 text-white font-bold uppercase tracking-widest text-sm py-4 border-2 border-zinc-950 hover:bg-zinc-800 transition-colors duration-100">
              View DLC Store
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-10 px-6 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="text-[10px] font-bold text-zinc-300 uppercase tracking-widest leading-loose">
                EA is a trademark of Electronic Arts. This site is a parody. Not affiliated with EA. But we did take notes.
              </p>
              <p className="text-[10px] font-medium text-zinc-300 mt-1">
                <a href="https://itorzo.com" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-500 transition-colors">An Itorzo product</a>
              </p>
            </div>
            <div className="flex items-center gap-5 shrink-0">
              <a href="/blog" className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest hover:text-zinc-950 transition-colors">Blog</a>
              <a href="/privacy" className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest hover:text-zinc-950 transition-colors">Privacy Policy</a>
              <a href="/terms" className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest hover:text-zinc-950 transition-colors">Terms</a>
              <a href="/about" className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest hover:text-zinc-950 transition-colors">About</a>
              <a href="/contact" className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest hover:text-zinc-950 transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
